import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import * as adminApi from '@/services/endpoints/admin'
import type { MaintenancePayload, MaintenanceState } from '@/types/admin'

/**
 * Maintenance switch, flipped by an admin from the panel.
 *
 * The state is public (`GET /admin/maintenance` needs no token) because the
 * notice page must render for anonymous visitors too. The API enforces the
 * block on its side as well — this store only decides what the app shows.
 */
export const useMaintenanceStore = defineStore('maintenance', () => {
  const state = ref<MaintenanceState | null>(null)
  const loading = ref(false)
  const loaded = ref(false)

  const isActive = computed(() => state.value?.enabled === true)

  /** Parsed `ends_at`, or null when open-ended (or unparseable). */
  const endsAt = computed<Date | null>(() => {
    const raw = state.value?.ends_at
    if (!raw) return null
    const parsed = new Date(raw)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  })

  const message = computed(() => state.value?.message?.trim() || null)

  async function fetch(force = false): Promise<MaintenanceState | null> {
    if (loaded.value && !force) return state.value
    loading.value = true
    try {
      state.value = await adminApi.getMaintenance()
      return state.value
    } catch {
      // An unreachable status endpoint must not lock anybody out: treat the
      // site as open and let the API's own 503 be the source of truth.
      state.value = null
      return null
    } finally {
      loaded.value = true
      loading.value = false
    }
  }

  /** Admin action: flips the switch and keeps the local copy in sync. */
  async function update(payload: MaintenancePayload): Promise<MaintenanceState> {
    const updated = await adminApi.setMaintenance(payload)
    state.value = updated
    loaded.value = true
    return updated
  }

  return { state, loading, loaded, isActive, endsAt, message, fetch, update }
})
